import { useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { draftIdIn } from 'components/create-dialog/useCreateDraft';
import AddContactModal from 'components/modals/contact/AddContactModal';
import { useModal } from 'components/modals/useModal';
import { FORM_OVERFLOW_LARGE_MODAL_STYLE } from 'components/modals/util';
import { format, parseISO } from 'date-fns';
import { defaultCurrencyOption, useDefaultCurrencyCode } from 'hooks/useDefaultCurrencyCode';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { StagedPricelists } from 'pages/pricelists/modal/MatterPricelistEditorModal';
import { useCaseRepricingConfirmation } from 'pages/pricelists/modal/useCaseRepricingConfirmation';
import {
    baseTabWithRates,
    datedTabBlockingOpeningDate,
    pricedRoleRows,
    tabLabel,
} from 'pages/pricelists/modal/matterPricelistEditorUtils';
import { formatRateRange } from 'pages/pricelists/rateRange';
import { DEFAULT_DATE_FORMAT_PATTERN } from 'utils/dateFormat';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Path, PathValue, SetValueConfig, useForm, useWatch } from 'react-hook-form';
import { useGetContactByIdQuery } from 'store/api/contacts';
import { useGetContractByIdQuery, useGetContractsCountQuery } from 'store/api/contracts';
import { useIsOneDriveIntegrationEnabledQuery } from 'store/api/dmsTeam';
import { useGetEmployeeByIdQuery } from 'store/api/employees';
import { useGetPricelistByIdQuery } from 'store/api/pricelists';
import { useGetTenantConfigurationQuery } from 'store/api/tenants.ts';
import { ClassifierData } from 'types/classifiers';
import {
    ContactMinDto,
    ContactMinDtoWithRelationsDto,
    PartyRelationshipTypeCode,
    PartyToEmployeeRelationDto,
    PartyType,
    RelationshipTypeCode,
} from 'types/contacts';
import { ContractListItemMinDto } from 'types/contracts';
import { MatterDto, MatterForm, MatterStateType, PricelistTabDraft, PricingLimitationType } from 'types/matters';
import { NamedEntityMinDto } from 'types/other';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { useGetOptionLabelWithPartyNumberAndEmail } from 'utils/partyNameUtil';
import CustomizeInvoiceModal from '../../invoices/layout/views/general-tab/CustomizeInvoiceModal';
import { supportedCustomization } from '../../invoices/layout/views/general-tab/supportedCustomization';
import { emptyPrimaryPayerRow, primaryPayerParty } from '../matterPayers';
import { isStatusChangingToOfferWon, practiceAreasWithParentOf } from '../matterUtils';
import { useCurrencyChangePricing } from '../useCurrencyChangePricing';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { MultiSelectChangeReason } from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import { ContactTypePicker } from 'components/modals/contact/ContactTypePicker';
import { unblockedClientsFilter } from 'components/tables/list-table/filter-pieces/filterConstants';
import useAuth from 'hooks/useAuth.ts';
import { getAdministrators, getManagers, getPartners } from 'pages/contacts/contactRelationUtils';
import { mapToContactMinDto } from 'pages/contacts/util.tsx';
import { useGetExperienceRecordsWithFiltersQuery } from 'store/api/experience-records.ts';
import { useGetInvoiceSpecTemplatesQuery, useGetInvoiceTemplatesQuery } from 'store/api/invoices';
import { ContractStatusType, ContractType } from 'types/contracts.ts';
import { CustomField } from 'types/customFields';
import { OfficeDocumentTemplateDto } from 'types/document-templates.ts';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features';
import { OfficeMin } from 'types/offices.ts';
import { PracticeArea } from 'types/practice-areas';
import { RSQL_AND } from 'utils/filterUtil.ts';
import { useCreateMatterPermissions, useHasPermissions } from 'utils/permissionUtil.ts';
import { buildTimecardIncrementOptions, toTimecardIncrementOption } from 'utils/timecardIncrementOptions.ts';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { useRequiredFields } from 'utils/useRequiredFields';
import { useAdministratorField } from 'hooks/useAdministratorField';
import { useStagedPricingLink } from 'pages/matters/form-sections/useStagedPricingLink';

/** The fields a contact created from a picker's "Add new contact" is put into: a party list, or one payer. */
type NewContactField =
    | 'opponentParties'
    | 'otherParties'
    | 'targetParties'
    | 'representedClients'
    | 'payerParty'
    | `payers.${number}.party`;

/** A blank case, as the create form starts. */
export const EMPTY_MATTER_FORM: MatterForm = {
    readOnlyCaseNumber: null,
    name: '',
    clientParty: null,
    matterStateType: null,
    matterType: null,
    reasonOfferLost: null,
    outcomeNotes: '',
    offerValueType: null,
    offerValueLow: '',
    offerValueHigh: '',
    estimatedEngagementLength: '',
    estimatedEngagementUnit: null,
    originatesFromMatterId: null,
    handledByOffice: null,
    generalNotes: '',
    officialReference: '',
    isUsedTimeUsedForRemuneration: false,
    isConfidential: false,
    confidentialReason: '',
    description: '',
    insiderInfo: '',
    isInsider: false,
    practiceAreas: [],
    secondLevelPracticeAreas: [],
    dmsPracticeArea: null,
    serviceAgreementContract: null,
    timecardIncrement: null,
    project: null,
    openedDate: null,
    closedDate: null,
    responsibleEmployee: null,
    supervisedByEmployee: null,
    employees: [],
    administrators: [],
    primaryContactParty: null,
    representedClients: [],
    isNoOpponent: false,
    opponentParties: [],
    targetParties: [],
    otherParties: [],
    judgeParties: [],
    archivedInOffice: null,
    archivedDate: null,
    archivingNotes: '',
    archivingPropertiesType: null,
    discountPct: '',
    discountExpiryDate: null,
    isCostIncluded: false,
    pricingLimitationType: null,
    accountingRevenueType: null,
    capUnitType: null,
    capLimit: '',
    fixedFee: '',
    recurringFixedFee: '',
    billingNotes: '',
    invoicingInfo: '',
    timecardLanguage: null,
    primaryRecipientParty: null,
    primaryRecipientAdditionalEmails: '',
    secondaryRecipientParties: [],
    secondaryRecipientAdditionalEmails: '',
    matterClientReference: '',
    invoiceMatterName: '',
    invoiceMatterNameLocalLanguage: '',
    invoiceTemplate: null,
    invoicedByOffice: null,
    invoicingIntervalType: null,
    payerParty: null,
    payers: [emptyPrimaryPayerRow()],
    invoiceSpecTemplate: null,
    referencedPricelist: null,
    pricelists: [baseTabWithRates()],
    pricelistCurrencyType: null,
    paymentCurrencyType: null,
    isPaymentCurrencyDifferent: false,
    isJointInvoicingDisabled: false,
    isAutomaticClosingExempt: false,
    matterBlockingPolicyType: null,
    isAmlBlockingExempt: false,
    nextInvoicingDate: null,
    paymentNotificationReceivers: [],
    dmsLink: '',
    isAccessibleFirmWide: false,
    isExcludedFromXR: false,
    preserveNonBillableHours: false,
    isPaperFile: false,
    invoiceCustomization: null,
    isSpecIncludedInInvoice: false,
    isSpecExcludedFromEmail: false,
    changeReasons: null,
};

/** The classifiers the case form reads. */
export const MATTER_FORM_CLASSIFIERS = [
    'ACCOUNTING_REVENUE_TYPE',
    'ARCHIVING_PROPERTIES_TYPE',
    'MATTER_STATE_TYPE',
    'MATTER_TYPE',
    'MATTER_BLOCKING_POLICY_TYPE',
    'REASON_OFFER_LOST',
    'OFFER_VALUE_TYPE',
    'ENGAGEMENT_LENGTH_UNIT',
    'LANGUAGE',
    'CURRENCY',
    'PRICING_LIMITATION_TYPE',
    'CAP_UNIT_TYPE',
    'INVOICE_TEMPLATE_TYPE',
    'INVOICING_INTERVAL_TYPE',
    'JOB_ROLE_GROUP_TYPE',
    'CONTRACT_STATUS_TYPE',
];

export function isClosingStatus(matterStateTypeCode: string | undefined) {
    return matterStateTypeCode === MatterStateType.COMPLETED || matterStateTypeCode === MatterStateType.OFFER_LOST;
}

export function isOfferOutcomeStatus(matterStateTypeCode: string | undefined) {
    return matterStateTypeCode === MatterStateType.OFFER_LOST || matterStateTypeCode === MatterStateType.OFFER_WON;
}

// The form initialisers coerce the nullable DTO amounts to '', so absence has to be tested as emptiness.
function hasAmount(value: string | number | null | undefined) {
    return value !== '' && value !== null && value !== undefined;
}

function isOfferStageStatus(matterStateTypeCode: string | undefined) {
    return isOfferOutcomeStatus(matterStateTypeCode) || matterStateTypeCode === MatterStateType.OFFER_PENDING;
}

// Lost, won and completed cases keep their prices: neither the lists nor the currency can be edited.
export function isPricingLocked(matterStateTypeCode: string | undefined) {
    return isOfferOutcomeStatus(matterStateTypeCode) || matterStateTypeCode === MatterStateType.COMPLETED;
}

export const isBaseTabPriced = (tabs: PricelistTabDraft[]): boolean => {
    const base = tabs.find((tab) => tab.isBase);
    return !!base && (pricedRoleRows(base.rates).length > 0 || base.personalRates.length > 0);
};

/**
 * Who has the case form's remuneration and insider fields. The case Overview asks too, so its row stands in
 * as text exactly where the form leaves the field out.
 */
export const useMatterFieldGates = () => {
    const isRemunerationFieldEditable = useHasPermissions(['UPDATE.MATTERS.REMUNERATION']);
    const hasRemunerationFieldAccess = useHasPermissions(['FIELD_ACCESS.TIMECARD.REMUNERATION_TIME']);
    // The backend blanks the insider info and the insider case tick without READ and ignores them without UPDATE.
    const canReadInsiderInfo = useHasPermissions(['READ.MATTER.INSIDER_LIST']);
    const canUpdateInsiderInfo = useHasPermissions(['UPDATE.MATTER.INSIDER_LIST']);
    return {
        shouldShowRemunerationField: isRemunerationFieldEditable || hasRemunerationFieldAccess,
        isRemunerationFieldEditable,
        canReadInsiderInfo,
        canUpdateInsiderInfo,
    };
};

export type MatterFormProps = {
    type: 'add' | 'edit';
    initialData?: MatterForm;
    classifiers: Record<string, ClassifierData>;
    currencyAccessible?: boolean;
    hasConnectedInvoices?: boolean;
    matter?: MatterDto;
    entityCustomFields?: CustomField[];
};

/**
 * The case form's state, rules and handlers: its prefills, locks, permissions and the change reasons. The full
 * form and a case section both run it, so a section's fields behave as they do on the full form; the section
 * components in this folder render its fields.
 */
export const useMatterForm = ({
    type = 'add',
    initialData,
    classifiers,
    currencyAccessible,
    hasConnectedInvoices,
    matter,
    entityCustomFields,
}: MatterFormProps) => {
    const {
        params: { id: matterId },
        location,
        inDialog,
    } = useCreateRoute();
    const isEditMode = type === 'edit';
    // Such a Case arrives fully prefilled from the Offer Won Case it continues, so the defaults that a
    // blank add form applies on mount must not overwrite what was carried over.
    const isOpenedFromOfferWonCase = initialData?.originatesFromMatterId != null;
    // A draft's add form holds what was saved: the defaults of a blank one would overwrite it as well.
    const isDraft = type === 'add' && draftIdIn(location.search) !== null;
    const { openModal, closeModal } = useModal();
    const effectiveFormat = useEffectiveDateFormat();
    const getOptionLabelWithPartyNumberAndEmail = useGetOptionLabelWithPartyNumberAndEmail();
    const { user } = useAuth();
    const userId = user?.id;
    const enabledFeatures = user?.enabledFeatures;
    const { data: currentEmployeeData } = useGetEmployeeByIdQuery(userId ? userId.toString() : skipToken);
    const isAccountingRevenueTypeEnabled =
        enabledFeatures && enabledFeatures.includes(FeatureFlag.MATTER_ACCOUNTING_REVENUE_TYPE);
    const isSubPracticeAreaEnabled = useIsFeatureEnabled(FeatureFlag.SUB_PRACTICE_AREA);
    const isSharedPaymentEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_SHARED_PAYMENT_FUNCTIONALITY);
    const canManagePayers = useHasPermissions(['UPDATE.MATTERS.ADD_AND_MANAGE_PAYERS']);
    // A list of several payers is read-only to those who may not manage payers; one payer they still pick.
    const hasStoredPayerList = (initialData?.payers.filter((payer) => payer.party).length ?? 0) > 1;
    const initialClientPartyId = initialData?.clientParty?.id;
    const isSinglePracticeArea = useIsFeatureEnabled(FeatureFlag.CASE_PA_FIELD_SINGLE_SELECTION_ONLY);
    // The administrative case is created by the backend without most of what a user-created case needs.
    const isAdministrativeMatter = matter?.isAdministrativeMatter === true;
    // The Case form's Required fields card; an administrative case is asked for none of them.
    const { isRequired } = useRequiredFields();
    const isDMSPracticeAreaMandatory = isRequired('CASE', 'DMS_PRACTICE_AREA') && !isAdministrativeMatter;
    const isMatterCreationClientsOnly = useIsFeatureEnabled(FeatureFlag.MATTER_CREATION_CLIENTS_ONLY);
    const hideDontNullifyOption = useIsFeatureEnabled(FeatureFlag.HIDE_DONT_NULLIFY_OPTION);
    const isLinkDraftContractsEnabled = useIsFeatureEnabled(FeatureFlag.LINK_DRAFT_CONTRACTS_WITH_CASES);
    const isPrimaryContactRequired = isRequired('CASE', 'PRIMARY_CONTACT') && !isAdministrativeMatter;
    const isPracticeAreaMandatoryOnCase = isRequired('CASE', 'PRACTICE_AREAS') && !isAdministrativeMatter;
    const isCaseDefaultStatusOfferPending = useIsFeatureEnabled(FeatureFlag.CASE_DEFAULT_STATUS_OFFER_PENDING);
    const { isOn: isAdministratorFieldEnabled, caseLabel: administratorFieldLabel } = useAdministratorField();
    const isSupervisorAndLawyerPrefillEnabled = useIsFeatureEnabled(FeatureFlag.CASE_PREFILL_RP_AS_SP_RM_AS_RL);
    const isAdministratorMandatoryForClients = isRequired('CASE', 'ASSISTANT') && !isAdministrativeMatter;
    const isOfferFlowEnabled = useIsFeatureEnabled(FeatureFlag.CASE_OFFER_FLOW);
    const isContractSelectionMandatory = isRequired('CASE', 'SERVICE_AGREEMENT');
    const isMatterTypeMandatory = isRequired('CASE', 'CASE_TYPE');
    const isResponsibleLawyerNotMandatory = !isRequired('CASE', 'RESPONSIBLE_LAWYER') || isAdministrativeMatter;
    const isTimecardIncrementControlEnabled = useIsFeatureEnabled(FeatureFlag.TIMECARD_INCREMENT_CONTROL);
    const { data: tenantConfiguration } = useGetTenantConfigurationQuery(undefined, {
        skip: !isTimecardIncrementControlEnabled && type !== 'add',
    });
    const { data: isOneDriveIntegrationEnabled } = useIsOneDriveIntegrationEnabledQuery();
    const {
        hasCreateMatters,
        hasAnyCreateMatterPermission,
        canCreateForClients,
        canCreateForLeads: hasLeadCreatePermission,
    } = useCreateMatterPermissions();
    const canCreateForLeads = !isMatterCreationClientsOnly && hasLeadCreatePermission;

    const [clientNameState, setClientNameState] = useState<{ prev: string | null; current: string | null }>({
        prev: null,
        current: initialData?.clientParty?.name ?? null,
    });

    const [caseNameState, setCaseNameState] = useState<{ prev: string | null; current: string | null }>({
        prev: null,
        current: initialData?.name ?? null,
    });

    const { data: invoiceTemplates, isLoading: isLoadingInvoiceTemplates } = useGetInvoiceTemplatesQuery();
    const { data: invoiceSpecTemplates, isLoading: isLoadingInvoiceSpecTemplates } = useGetInvoiceSpecTemplatesQuery();
    const { data: xrs, isLoading: isLoadingXRs } = useGetExperienceRecordsWithFiltersQuery(
        matterId !== undefined
            ? {
                  filters: `matterId==${matterId}${RSQL_AND}hasExperienceRecord==true`,
                  page: 0,
                  pageSize: 1,
              }
            : skipToken
    );

    const methods = useForm<MatterForm>({
        defaultValues: initialData ? { ...EMPTY_MATTER_FORM, ...initialData } : EMPTY_MATTER_FORM,
        values: initialData,
    });

    const {
        control,
        setValue,
        getValues,
        register,
        resetField,
        clearErrors,
        trigger,
        formState: { errors },
    } = methods;

    // What a new case fills in by itself is a default, not a field the user filled: once anything reads `isDirty`,
    // react-hook-form compares every value with the defaults, so a set value would leave the form filled for good.
    // `resetField` skips a field no input has registered, as while the form's loader shows: `register` adds it.
    const fillIn = useCallback(
        <N extends Path<MatterForm>>(name: N, value: PathValue<MatterForm, N>) => {
            if (isEditMode) {
                setValue(name, value);
                return;
            }
            register(name);
            resetField(name, { defaultValue: value });
        },
        [isEditMode, register, resetField, setValue]
    );

    const { customFields, mapCustomFieldsToDto, customFieldValuesOf } = useCustomFields({
        entityType: EntityType.MATTER,
        fields: entityCustomFields,
    });

    const [officeInvoiceTemplates, setOfficeInvoiceTemplates] = useState<OfficeDocumentTemplateDto[]>([]);
    const [officeSpecTemplates, setOfficeSpecTemplates] = useState<OfficeDocumentTemplateDto[]>([]);

    // Watch values to dynamically render certain fields
    const [
        matterStateType,
        pricingLimitationType,
        clientParty,
        isNoOpponent,
        serviceAgreementContract,
        handledByOffice,
        isConfidential,
        isAccessibleFirmWide,
        matterBlockingPolicyType,
        isAmlBlockingExempt,
        isPaymentCurrencyDifferent,
        invoicingIntervalType,
    ] = useWatch({
        control,
        name: [
            'matterStateType',
            'pricingLimitationType',
            'clientParty',
            'isNoOpponent',
            'serviceAgreementContract',
            'handledByOffice',
            'isConfidential',
            'isAccessibleFirmWide',
            'matterBlockingPolicyType',
            'isAmlBlockingExempt',
            'isPaymentCurrencyDifferent',
            'invoicingIntervalType',
        ],
    });
    const practiceAreas = useWatch({ control, name: 'practiceAreas' });
    const secondLevelPracticeAreas = useWatch({ control, name: 'secondLevelPracticeAreas' });
    const referencedPricelist = useWatch({ control, name: 'referencedPricelist' });
    const pricelistCurrencyType = useWatch({ control, name: 'pricelistCurrencyType' });
    const payerParty = useWatch({ control, name: 'payerParty' });
    const payers = useWatch({ control, name: 'payers' });

    // The payer list's first row is the payer; the legacy field follows it so everything reading it keeps working.
    useEffect(() => {
        if (!isSharedPaymentEnabled) {
            return;
        }
        const primary = primaryPayerParty(payers ?? []);
        if ((primary?.id ?? null) !== (getValues('payerParty')?.id ?? null)) {
            setValue('payerParty', primary);
        }
    }, [isSharedPaymentEnabled, payers, getValues, setValue]);
    const timecardIncrement = useWatch({ control, name: 'timecardIncrement' });
    const isSpecIncludedInInvoice = useWatch({ control, name: 'isSpecIncludedInInvoice' });

    // Offer estimates belong to the offer stage, but stay visible on a case that already carries them so a stored
    // value can still be corrected or cleared after the case moves on.
    const showOfferEstimateFields =
        isOfferFlowEnabled &&
        (isOfferStageStatus(matterStateType?.value) ||
            hasAmount(initialData?.offerValueLow) ||
            hasAmount(initialData?.offerValueHigh) ||
            hasAmount(initialData?.estimatedEngagementLength));

    const validContractsFilter = `contractStatusTypeCode==${ContractStatusType.VALID};contractTypeCode==${ContractType.SERVICE_AGREEMENT};parties.party.id=in=(${clientParty?.id ?? -1})`;
    const { data: validContractsCount } = useGetContractsCountQuery(
        { filters: validContractsFilter },
        { skip: !isContractSelectionMandatory || !clientParty?.id }
    );

    // Calculate which tracked fields have changed (for change reason inputs)
    const changeReasonFields = useMemo(() => {
        if (!isEditMode || !initialData) {
            return {
                matterBlockingPolicyType: false,
                isAmlBlockingExempt: false,
            };
        }
        const currentBlockingValue = matterBlockingPolicyType?.value;
        const initialBlockingValue = initialData.matterBlockingPolicyType?.value;
        return {
            matterBlockingPolicyType: currentBlockingValue !== initialBlockingValue,
            isAmlBlockingExempt: isAmlBlockingExempt !== (initialData.isAmlBlockingExempt ?? false),
        };
    }, [isEditMode, initialData, matterBlockingPolicyType, isAmlBlockingExempt]);

    const practiceAreaForDMSOptions = isSubPracticeAreaEnabled ? secondLevelPracticeAreas : practiceAreas;
    const invoicingIntervalTypeOptions = useMemo(
        () =>
            (getValidOptionsFromClassifier(classifiers['INVOICING_INTERVAL_TYPE']) as {
                label: string;
                value: string;
            }[]) ?? [],
        [classifiers]
    );

    // "Success fee" option is hidden until further analysis is made
    const pricingLimitationTypeOptions = useMemo(
        () =>
            (
                (getValidOptionsFromClassifier(classifiers['PRICING_LIMITATION_TYPE']) as {
                    label: string;
                    value: string;
                }[]) ?? []
            ).filter((option) => option.value !== PricingLimitationType.SUCCESS_FEE),
        [classifiers]
    );

    const secondLevelPracticeAreasFilter = useMemo(() => {
        if (!practiceAreas || practiceAreas.length === 0) {
            return 'showInCases==true;parentPracticeArea.showInCases==true';
        }
        const parentIds = practiceAreas.map((pa) => pa.id).join(',');
        return `parentPracticeArea.id=in=(${parentIds});showInCases==true`;
    }, [practiceAreas]);

    // While a case takes one practice area, the sub-area list offers only what a pick can add. A first
    // sub-area sets the area with the list still open: the loaded options handleSecondLevelPracticeAreaSelection
    // would refuse are dropped at once, and the list is asked again under the filter above.
    const secondLevelPracticeAreasNarrowing = useMemo(
        () =>
            isSinglePracticeArea
                ? {
                      refetchOnFiltersChange: true,
                      transformOptions: (subAreas: PracticeArea[]) =>
                          subAreas.filter(
                              (subArea) => practiceAreasWithParentOf(subArea, practiceAreas ?? [], true) !== null
                          ),
                  }
                : undefined,
        [isSinglePracticeArea, practiceAreas]
    );

    const allowedRelationshipTypes = useMemo(() => {
        const types: RelationshipTypeCode[] = [];
        if (canCreateForClients) types.push(RelationshipTypeCode.CLIENT);
        if (canCreateForLeads) types.push(RelationshipTypeCode.LEAD);
        return types;
    }, [canCreateForClients, canCreateForLeads]);

    const clientPartyFilter = useMemo(() => {
        if (allowedRelationshipTypes.length === 0) return '';
        return `relationshipTypeCode=in=(${allowedRelationshipTypes.join(',')});${unblockedClientsFilter}`;
    }, [allowedRelationshipTypes]);

    // Reset fixedFee and capLimit according to pricingLimitationType
    // TODO: cascading-set-state skip — RHF `setValue`/`clearErrors` framework calls keyed off pricingLimitationType; multiple independent fields.
    useEffect(() => {
        if (pricingLimitationType?.value !== PricingLimitationType.FIXED_ONETIME) {
            setValue('fixedFee', '');
        }

        if (pricingLimitationType?.value !== PricingLimitationType.NON_BILLABLE) {
            setValue('preserveNonBillableHours', false);
        }

        if (pricingLimitationType?.value !== PricingLimitationType.FIXED_RECURRING) {
            setValue('recurringFixedFee', '');
            clearErrors('invoicingIntervalType');
        }

        if (pricingLimitationType?.value !== PricingLimitationType.HOURLY_CAPPED) {
            setValue('capLimit', '');
            setValue('capUnitType', null);
        }
    }, [pricingLimitationType, setValue, clearErrors]);

    // On a page the client the form started with is let through, so a case that already has that client can
    // still be saved. In the create dialog a flagged client blocks however it arrived.
    const isNewPersonaNonGrataClient =
        clientParty?.isPersonaNonGrata && (inDialog || initialData?.clientParty?.id !== clientParty.id);

    const { currentData: clientPartyFullData, isFetching: isFetchingCompanyData } = useGetContactByIdQuery(
        clientParty?.id?.toString() ?? skipToken
    );
    const isLoadingNewClientData = isFetchingCompanyData && clientPartyFullData === undefined;
    const isInternalClient = clientPartyFullData?.isInternalClient ?? false;
    // What the client a new case opened with brings is the form's own, a default; one the user picks brings values.
    const isOpenedWithClient =
        !isEditMode && initialClientPartyId != null && clientPartyFullData?.id === initialClientPartyId;
    const fillFromClient = useCallback(
        <N extends Path<MatterForm>>(name: N, value: PathValue<MatterForm, N>, options?: SetValueConfig) =>
            isOpenedWithClient ? fillIn(name, value) : setValue(name, value, options),
        [isOpenedWithClient, fillIn, setValue]
    );

    // Internal clients and cases still at the offer stage are exempt from mandatory service-agreement
    // selection even when the FF requires it.
    const isOfferStageCase =
        matterStateType?.value === MatterStateType.OFFER_PENDING ||
        matterStateType?.value === MatterStateType.OFFER_LOST;
    const isServiceAgreementRequired =
        isContractSelectionMandatory &&
        !isAdministrativeMatter &&
        !isInternalClient &&
        !isOfferStageCase &&
        (validContractsCount?.count ?? 0) > 0;

    const { data: selectedPricelistData } = useGetPricelistByIdQuery(referencedPricelist?.id ?? skipToken);

    // In edit mode the contract details are only needed to resolve the timecard increment;
    // the other contract-driven prefills below bail out of edit mode themselves.
    const { data: contractFullData } = useGetContractByIdQuery(`${serviceAgreementContract?.id}` || '', {
        skip:
            (type === 'edit' && !isTimecardIncrementControlEnabled) ||
            !serviceAgreementContract ||
            isNaN(Number(serviceAgreementContract?.id)),
    });

    // The kind the create keeps when the form names none: the one the price list field shows. Unknown while the
    // service agreement it may come from is still loading.
    const [stagedPricingKind, stagedFollowsPricelist, stagedPricelists] = useWatch({
        control,
        name: ['pricingKind', 'followsPricelist', 'pricelists'],
    });
    const stagedLink = useStagedPricingLink({
        isCreate: type !== 'edit',
        pricingKind: stagedPricingKind,
        followsPricelist: stagedFollowsPricelist,
        pricelists: stagedPricelists,
        serviceAgreementContractId: serviceAgreementContract?.id,
    });
    const shownPricingKind =
        type !== 'edit' && (!serviceAgreementContract || contractFullData?.id === serviceAgreementContract.id)
            ? stagedLink.pricingKind
            : undefined;

    // A supervisor, responsible lawyer or assistant the user picks joins Handled by; changing or clearing the pick
    // leaves Handled by as it is.
    const prefillHandledBy = useCallback(
        (employee: NamedEntityMinDto | null) => {
            const handleBy = getValues('employees');
            if (employee && !handleBy.find((val) => val.id === employee.id)) handleBy.push(employee);
            setValue('employees', handleBy);
        },
        [getValues, setValue]
    );

    // What the client's relationship prefill wrote, so a client switch can take back exactly that.
    // For the assistants, the ids of those it added to Handled by.
    const clientStaffingPrefillRef = useRef<
        Partial<Record<'supervisedByEmployee' | 'responsibleEmployee', { id: number; joinedHandledBy: boolean }>> & {
            administrators?: number[];
        }
    >({});

    // Prefill  if empty and client party changes
    // TODO: cascading-set-state skip — multiple independent RHF prefill fields driven by async clientPartyFullData; refactor would touch many call sites.
    useEffect(() => {
        // On a new case what a picked client brings is not a field the user filled either: the pick is.
        const prefill = { shouldDirty: isEditMode };
        const accountingRevenueType = getValues('accountingRevenueType');
        if (
            isAccountingRevenueTypeEnabled &&
            !accountingRevenueType &&
            !!clientPartyFullData?.accountingRevenueTypeCode
        ) {
            fillFromClient(
                'accountingRevenueType',
                (
                    getValidOptionsFromClassifier(classifiers['ACCOUNTING_REVENUE_TYPE']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === clientPartyFullData?.accountingRevenueTypeCode) ?? null
            );
        }

        const payerParty = getValues('payerParty');
        // The payer list shows a case's stored payers when the editor opens; only a client the user picks brings
        // its contact card's payer along.
        const isClientPicked = !isEditMode || clientPartyFullData?.id !== initialClientPartyId;
        if (!payerParty && !!clientPartyFullData?.payerParty && (!isSharedPaymentEnabled || isClientPicked)) {
            if (isSharedPaymentEnabled) {
                const payerRows = getValues('payers');
                fillFromClient(
                    'payers',
                    payerRows.map((row, index) =>
                        index === 0 && !row.party ? { ...row, party: clientPartyFullData.payerParty } : row
                    ),
                    prefill
                );
            }
            fillFromClient('payerParty', clientPartyFullData.payerParty, prefill);
        }

        const currentPrimaryRecipientAdditionalEmails = getValues('primaryRecipientAdditionalEmails');
        if (!currentPrimaryRecipientAdditionalEmails && !!clientPartyFullData?.primaryRecipientAdditionalEmails) {
            fillFromClient(
                'primaryRecipientAdditionalEmails',
                clientPartyFullData.primaryRecipientAdditionalEmails,
                prefill
            );
        }

        const currentSecondaryRecipientParties = getValues('secondaryRecipientParties');
        if (
            currentSecondaryRecipientParties.length === 0 &&
            clientPartyFullData &&
            (clientPartyFullData?.relations || []).filter(
                (relation) => relation.partyRelationshipTypeCode === PartyRelationshipTypeCode.SECONDARY_RECIPIENT
            ).length > 0
        ) {
            const parties = (clientPartyFullData.relations || [])
                .filter(
                    (relation) => relation.partyRelationshipTypeCode === PartyRelationshipTypeCode.SECONDARY_RECIPIENT
                )
                .map((relation) => relation.otherParty);
            fillFromClient(
                'secondaryRecipientParties',
                parties.map(({ id, name, mainEmail }) => ({ id, name, email: mainEmail ?? '' })),
                prefill
            );
        }

        const currentSecondaryRecipientAdditionalEmails = getValues('secondaryRecipientAdditionalEmails');
        if (!currentSecondaryRecipientAdditionalEmails && !!clientPartyFullData?.secondaryRecipientAdditionalEmails) {
            fillFromClient(
                'secondaryRecipientAdditionalEmails',
                clientPartyFullData.secondaryRecipientAdditionalEmails,
                prefill
            );
        }

        // The client's Client assistants seed the case's Case assistant field, and only while the
        // field is still empty — the same "don't overwrite what the user picked" rule the prefills
        // above follow. Reads the assistants off the full client payload, so it lands once that
        // resolves rather than on the picker's min DTO. On a new case they join Handled by as a manual pick does.
        // Inactive and deleted employees never join Handled by, Supervised by or Responsible lawyer: the pickers never
        // offer them.
        const pickable = (relations: PartyToEmployeeRelationDto[] | null) =>
            relations?.filter(({ employee }) => employee.isActive !== false && !employee.deletedAt) ?? null;
        const currentAdministrators = getValues('administrators');
        if (
            isAdministratorFieldEnabled &&
            currentAdministrators.length === 0 &&
            clientPartyFullData?.administrators?.length
        ) {
            const assistants = getAdministrators(clientPartyFullData.administrators);
            fillFromClient('administrators', assistants, prefill);
            if (type === 'add') {
                const handledBy = getValues('employees');
                const joined = getAdministrators(pickable(clientPartyFullData.administrators)).filter(
                    ({ id }) => !handledBy.some((employee) => employee.id === id)
                );
                if (joined.length) {
                    fillFromClient('employees', [...handledBy, ...joined], prefill);
                }
                clientStaffingPrefillRef.current.administrators = joined.map(({ id }) => id);
            }
        }

        // On a new case the client's first relationship partner seeds Supervised by and its first
        // relationship manager Responsible lawyer, each only while empty and joining Handled by as a
        // manual pick does.
        if (isSupervisorAndLawyerPrefillEnabled && type === 'add' && clientPartyFullData) {
            const prefillRole = (
                field: 'supervisedByEmployee' | 'responsibleEmployee',
                employee: NamedEntityMinDto | undefined
            ) => {
                if (!employee || getValues(field)) return;
                const joinedHandledBy = !getValues('employees').some(({ id }) => id === employee.id);
                fillFromClient(field, employee, prefill);
                if (joinedHandledBy) {
                    fillFromClient('employees', [...getValues('employees'), employee], prefill);
                }
                clientStaffingPrefillRef.current[field] = { id: employee.id, joinedHandledBy };
            };
            prefillRole('supervisedByEmployee', getPartners(pickable(clientPartyFullData.partners))[0]);
            prefillRole('responsibleEmployee', getManagers(pickable(clientPartyFullData.managers))[0]);
        }

        if (clientPartyFullData?.isInternalClient && type === 'add') {
            fillFromClient(
                'pricingLimitationType',
                (
                    getValidOptionsFromClassifier(classifiers['PRICING_LIMITATION_TYPE']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === PricingLimitationType.NON_BILLABLE) ?? null
            );
        }
        if (clientPartyFullData !== undefined) {
            if (!clientPartyFullData.isInternalClient) {
                setValue('isAccessibleFirmWide', false);
            }
        }
    }, [
        clientPartyFullData,
        initialClientPartyId,
        isAccountingRevenueTypeEnabled,
        isAdministratorFieldEnabled,
        isEditMode,
        isSharedPaymentEnabled,
        isSupervisorAndLawyerPrefillEnabled,
        classifiers,
        getValues,
        setValue,
        fillFromClient,
        type,
    ]);

    // A blank new case starts in the firm's default currency. While the field still holds that fill, a price list or a
    // service agreement chosen later sets its own currency, as it fills an empty field; a currency the user picks stays.
    // Edits, copies, drafts and Offer won follow-ups never take the fill, so their stored currency is never replaced.
    const { code: defaultCurrencyCode } = useDefaultCurrencyCode();
    const defaultCurrencyFillRef = useRef<string | null>(null);
    // Declared before the fill, so the mount that runs both cannot drop what the fill has just noted.
    useEffect(() => {
        if (pricelistCurrencyType?.value !== defaultCurrencyFillRef.current) {
            defaultCurrencyFillRef.current = null;
        }
    }, [pricelistCurrencyType]);
    useEffect(() => {
        if (type !== 'add' || isDraft || isOpenedFromOfferWonCase || getValues('pricelistCurrencyType')) return;
        const option = defaultCurrencyOption(
            getValidOptionsFromClassifier<string, string>(classifiers['CURRENCY']),
            defaultCurrencyCode
        );
        if (option) {
            defaultCurrencyFillRef.current = option.value;
            fillIn('pricelistCurrencyType', option);
        }
    }, [type, isDraft, isOpenedFromOfferWonCase, classifiers, defaultCurrencyCode, getValues, fillIn]);

    useEffect(() => {
        if (
            referencedPricelist?.id &&
            (!pricelistCurrencyType || pricelistCurrencyType.value === defaultCurrencyFillRef.current) &&
            selectedPricelistData?.currencyTypeCode &&
            selectedPricelistData.id === referencedPricelist.id
        ) {
            defaultCurrencyFillRef.current = null;
            setValue(
                'pricelistCurrencyType',
                (
                    getValidOptionsFromClassifier(classifiers['CURRENCY']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === selectedPricelistData.currencyTypeCode) ?? null,
                { shouldDirty: true }
            );
        }
    }, [referencedPricelist, pricelistCurrencyType, selectedPricelistData, setValue, classifiers]);

    // TODO: no-effect-event-handler — mutually exclusive flags; could move into the CheckboxInput onChange handlers
    useEffect(() => {
        if (isConfidential) {
            setValue('isAccessibleFirmWide', false);
        }
    }, [isConfidential, setValue]);

    // TODO: no-effect-event-handler — mutually exclusive flags; could move into the CheckboxInput onChange handlers
    useEffect(() => {
        if (isAccessibleFirmWide) {
            setValue('isConfidential', false);
        }
    }, [isAccessibleFirmWide, setValue]);

    // TODO: no-effect-event-handler — mutually exclusive flags; could move into the CheckboxInput onChange handlers
    useEffect(() => {
        if (isConfidential) {
            setValue('isJointInvoicingDisabled', true);
        }
    }, [isConfidential, setValue]);

    // A tenant that keeps its default Handled by for confidential cases has a new case show it once ticked
    // confidential, as the save will add it; unticking takes back exactly what the tick added.
    const confidentialDefaultsRef = useRef<number[]>([]);
    // A default the user takes out of Handled by is no longer the tick's: picked again, it is the user's and stays.
    // Declared before the tick's effect, so a mount that runs both cannot drop what the tick has just added.
    const handledBy = useWatch({ control, name: 'employees' });
    useEffect(() => {
        confidentialDefaultsRef.current = confidentialDefaultsRef.current.filter((id) =>
            handledBy.some((employee) => employee.id === id)
        );
    }, [handledBy]);
    useEffect(() => {
        if (type !== 'add' || !tenantConfiguration?.isDefaultHandledByOnlyWhenConfidential) return;
        const employees = getValues('employees');
        if (isConfidential) {
            const added = tenantConfiguration.defaultHandledByEmployees.filter(
                ({ id }) => !employees.some((employee) => employee.id === id)
            );
            if (added.length === 0) return;
            confidentialDefaultsRef.current.push(...added.map(({ id }) => id));
            setValue('employees', [...employees, ...added]);
        } else if (confidentialDefaultsRef.current.length > 0) {
            const added = confidentialDefaultsRef.current;
            confidentialDefaultsRef.current = [];
            setValue(
                'employees',
                employees.filter(({ id }) => !added.includes(id))
            );
        }
    }, [type, isConfidential, tenantConfiguration, getValues, setValue]);

    // TODO: cascading-set-state skip — multiple RHF prefills driven by async contractFullData; setValue framework API.
    useEffect(() => {
        if (type === 'edit') return;
        const [currentPricelistCurrencyTypeState, currentReferencedPricelistState] = getValues([
            'pricelistCurrencyType',
            'referencedPricelist',
        ]);

        if (
            (!currentPricelistCurrencyTypeState ||
                currentPricelistCurrencyTypeState.value === defaultCurrencyFillRef.current) &&
            contractFullData?.pricelist?.currencyTypeCode
        ) {
            defaultCurrencyFillRef.current = null;
            setValue(
                'pricelistCurrencyType',
                (
                    getValidOptionsFromClassifier(classifiers['CURRENCY']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === contractFullData?.pricelist?.currencyTypeCode) ?? null,
                { shouldDirty: true }
            );
        }

        if (!currentReferencedPricelistState && contractFullData?.pricelist?.id) {
            setValue(
                'referencedPricelist',
                {
                    id: contractFullData?.pricelist?.id,
                    pricingHash: contractFullData?.pricelist?.pricingHash ?? undefined,
                },
                { shouldDirty: true }
            );

            // The contract's rates fill the base tab's role rows only; other tabs and personal rows stay.
            const [base, ...dated] = getValues('pricelists');
            setValue('pricelists', [{ ...base, rates: contractFullData?.pricelist?.rates ?? [] }, ...dated], {
                shouldDirty: true,
            });
        }
    }, [classifiers, contractFullData, getValues, setValue, type]);

    const handlePrimaryContactChange = useCallback(
        (newContact: ContactMinDto | null) => {
            if (newContact?.id && newContact?.name && !getValues('primaryRecipientParty')) {
                setValue('primaryRecipientParty', {
                    id: newContact.id,
                    name: newContact.name,
                    email: '',
                });
            }
        },
        [getValues, setValue]
    );

    //prefill discountPct
    useEffect(() => {
        if (initialData?.discountPct) {
            return;
        }
        if (serviceAgreementContract?.discountPct) {
            setValue('discountPct', serviceAgreementContract.discountPct);
        }
    }, [serviceAgreementContract, initialData?.discountPct, setValue]);

    // Prefill the invoicing interval from the linked service agreement, falling back to the handling
    // office's company default. Depend on the resolved code (a stable primitive), not the watched
    // objects — their references churn on every form change, which would re-fire this setValue and
    // overwrite the interval the user just picked.
    const hasInvoicingIntervalPrefillSource = serviceAgreementContract != null || handledByOffice != null;
    const invoicingIntervalPrefillCode = serviceAgreementContract
        ? (serviceAgreementContract.contractInvoicingIntervalTypeCode ?? null)
        : (handledByOffice?.companyInvoicingIntervalTypeCode ?? null);
    const hasStoredInvoicingIntervalType = initialData?.invoicingIntervalType != null;
    const isProBono = pricingLimitationType?.value === PricingLimitationType.PRO_BONO;
    useEffect(() => {
        if (hasStoredInvoicingIntervalType || isProBono || !hasInvoicingIntervalPrefillSource) {
            return;
        }
        const next = invoicingIntervalTypeOptions.find((val) => val.value === invoicingIntervalPrefillCode) ?? null;
        if (next?.value === getValues('invoicingIntervalType')?.value) {
            return;
        }
        fillIn('invoicingIntervalType', next);
    }, [
        hasStoredInvoicingIntervalType,
        isProBono,
        hasInvoicingIntervalPrefillSource,
        invoicingIntervalPrefillCode,
        invoicingIntervalTypeOptions,
        getValues,
        fillIn,
    ]);

    // Tracks whether the user picked an increment manually; auto-prefills must not override that.
    const isTimecardIncrementTouchedRef = useRef(false);
    // Tracks whether the user changed the linked service agreement; until then a stored increment wins.
    const hasContractSelectionChangedRef = useRef(false);
    // Derived primitives keep the prefill effect deps stable across watched-object reference churn.
    const defaultTimecardIncrement = tenantConfiguration?.defaultTimecardIncrement ?? null;
    const initialTimecardIncrement = initialData?.timecardIncrement?.value ?? null;
    const selectedContractId = serviceAgreementContract?.id ?? null;
    const isSelectedContractTerminated =
        serviceAgreementContract?.contractStatusTypeCode === ContractStatusType.TERMINATED;
    // Contract details load asynchronously; only trust them once they belong to the selected contract.
    const isSelectedContractLoaded = selectedContractId != null && contractFullData?.id === selectedContractId;
    const selectedContractTimecardIncrement = isSelectedContractLoaded
        ? (contractFullData?.timecardIncrement ?? null)
        : null;

    // Prefill the timecard increment from the selected service agreement, falling back to the
    // tenant default when no contract is selected, the contract carries no increment, or the
    // linked contract is terminated. A user-picked increment always wins; a value stored on the
    // matter wins only until the user changes the linked service agreement.
    useEffect(() => {
        if (!isTimecardIncrementControlEnabled || defaultTimecardIncrement == null) {
            return;
        }
        if (isTimecardIncrementTouchedRef.current) {
            return;
        }
        if (initialTimecardIncrement != null && !hasContractSelectionChangedRef.current) {
            return;
        }

        const target =
            selectedContractId != null && !isSelectedContractTerminated
                ? isSelectedContractLoaded
                    ? (selectedContractTimecardIncrement ?? defaultTimecardIncrement)
                    : null // contract details still loading, decide once they arrive
                : defaultTimecardIncrement;

        if (target == null || getValues('timecardIncrement')?.value === target) {
            return;
        }
        fillIn('timecardIncrement', toTimecardIncrementOption(target));
    }, [
        isTimecardIncrementControlEnabled,
        defaultTimecardIncrement,
        initialTimecardIncrement,
        selectedContractId,
        isSelectedContractTerminated,
        isSelectedContractLoaded,
        selectedContractTimecardIncrement,
        getValues,
        fillIn,
    ]);

    const timecardIncrementOptions = useMemo(
        () => buildTimecardIncrementOptions(tenantConfiguration?.timecardIncrementValues, timecardIncrement?.value),
        [tenantConfiguration?.timecardIncrementValues, timecardIncrement?.value]
    );

    // Prefill the fee arrangement from the handling office's company default.
    // Depend on the resolved code (a stable primitive), not the handledByOffice object — its
    // reference churns across renders, which would otherwise re-fire this setValue on every commit.
    const handledByOfficeDefaultFeeArrangement = handledByOffice?.companyCasesDefaultFeeArrangementTypeCode ?? null;
    // Internal clients are forced to NON_BILLABLE elsewhere, so skip the office default in that case.
    useEffect(() => {
        if (
            type !== 'add' ||
            isDraft ||
            isInternalClient ||
            isOpenedFromOfferWonCase ||
            !handledByOfficeDefaultFeeArrangement
        ) {
            return;
        }
        fillIn(
            'pricingLimitationType',
            pricingLimitationTypeOptions.find((val) => val.value === handledByOfficeDefaultFeeArrangement) ?? null
        );
    }, [
        type,
        isDraft,
        isInternalClient,
        isOpenedFromOfferWonCase,
        handledByOfficeDefaultFeeArrangement,
        pricingLimitationTypeOptions,
        fillIn,
    ]);

    useEffect(() => {
        if (type === 'add' && handledByOffice && !getValues('invoicedByOffice')) {
            fillIn('invoicedByOffice', handledByOffice);
        }
    }, [type, handledByOffice, fillIn, getValues]);

    // TODO: no-effect-event-handler — initialization (prefill current user as employee on add); not a click-handler mirror
    useEffect(() => {
        if (currentEmployeeData) {
            const currentUserEmployee: NamedEntityMinDto = {
                id: currentEmployeeData.id,
                name:
                    currentEmployeeData.localFullName ||
                    `${currentEmployeeData.firstName} ${currentEmployeeData.lastName}`,
            };

            if (type === 'add' && !isDraft) {
                const currentEmployees = getValues('employees') || [];
                const isCurrentUserAlreadyInList = currentEmployees.some((emp) => emp.id === currentUserEmployee.id);
                if (!isCurrentUserAlreadyInList) {
                    fillIn('employees', [...currentEmployees, currentUserEmployee]);
                }
            }
        }
    }, [type, isDraft, currentEmployeeData, fillIn, getValues]);

    useEffect(() => {
        if (type === 'add' && !isDraft && isCaseDefaultStatusOfferPending && !isOpenedFromOfferWonCase) {
            const inProgressOption = (
                getValidOptionsFromClassifier(classifiers['MATTER_STATE_TYPE']) as {
                    label: string;
                    value: string;
                }[]
            ).find((option) => option.value === MatterStateType.OFFER_PENDING);

            if (inProgressOption) {
                fillIn('matterStateType', inProgressOption);
            }
        }
    }, [type, isDraft, isCaseDefaultStatusOfferPending, isOpenedFromOfferWonCase, classifiers, fillIn]);

    const prefillTemplates = useCallback(
        (office: OfficeMin | null) => {
            if (office?.documentTemplates && clientPartyFullData) {
                const officeInvoiceTemplates = office.documentTemplates.filter(
                    (template) => 'INVOICE' === template.documentTemplateType
                );
                const officeSpecTemplates = office.documentTemplates.filter(
                    (template) => 'INVOICE_SPEC' === template.documentTemplateType
                );
                setOfficeInvoiceTemplates(officeInvoiceTemplates);
                setOfficeSpecTemplates(officeSpecTemplates);
                const isLocalClient =
                    !!clientPartyFullData.registrationAddress?.countryCode &&
                    clientPartyFullData.registrationAddress.countryCode === office.address?.countryCode;
                const isOfficeDefault = (template: OfficeDocumentTemplateDto) =>
                    isLocalClient ? template.isDefault : template.isDefaultForeign;
                const clientInvoiceTemplate = invoiceTemplates?.find(
                    (template) => template.id === clientPartyFullData.invoiceTemplateId
                );
                const clientSpecTemplate = invoiceSpecTemplates?.find(
                    (template) => template.id === clientPartyFullData.invoiceSpecTemplateId
                );
                fillFromClient(
                    'invoiceTemplate',
                    clientInvoiceTemplate ?? officeInvoiceTemplates.find(isOfficeDefault) ?? null
                );
                fillFromClient(
                    'invoiceSpecTemplate',
                    clientSpecTemplate ?? officeSpecTemplates.find(isOfficeDefault) ?? null
                );
                // A client pick clears the customization, so one still here is the case's own or the user's.
                if (clientPartyFullData.invoiceCustomization && !getValues('invoiceCustomization')) {
                    fillFromClient('invoiceCustomization', clientPartyFullData.invoiceCustomization);
                }
            }
        },
        [clientPartyFullData, invoiceTemplates, invoiceSpecTemplates, getValues, fillFromClient]
    );

    // An edited case keeps its stored templates until the user picks a client; once picked, even the case's own
    // client again, the picker has cleared them.
    const isClientPickedRef = useRef(!isEditMode);

    // Keyed off the loaded client rather than the client picker: the picker's handler still holds the previous
    // client's card, so prefilling there would carry that client's templates over.
    useEffect(() => {
        if (clientParty?.id !== initialClientPartyId) {
            isClientPickedRef.current = true;
        }
        if (
            isClientPickedRef.current &&
            handledByOffice &&
            !isLoadingNewClientData &&
            !isLoadingInvoiceTemplates &&
            !isLoadingInvoiceSpecTemplates &&
            !getValues('invoiceTemplate') &&
            !getValues('invoiceSpecTemplate')
        ) {
            // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- prefill keyed off the async client read; the office's template lists are state because the office picker sets them too
            prefillTemplates(handledByOffice);
        }
    }, [
        clientParty?.id,
        initialClientPartyId,
        handledByOffice,
        isLoadingNewClientData,
        isLoadingInvoiceTemplates,
        isLoadingInvoiceSpecTemplates,
        getValues,
        prefillTemplates,
    ]);

    const isCaseMergeInvoiceSpecEnabled = useIsFeatureEnabled(FeatureFlag.CASE_MERGE_INVOICE_SPEC);

    const isUserPrivilegedToUpdateNonBillableHoursBehavior = useHasPermissions([
        'UPDATE.MATTERS.CHECKBOX_DONT_NULLIFY_NON_BILLABLE',
    ]);
    const isUserPrivilegedToUpdateBlockedFields = useHasPermissions(['UPDATE.MATTERS.BLOCK_TIMECARD_ENTRIES']);
    const { shouldShowRemunerationField, isRemunerationFieldEditable, canReadInsiderInfo, canUpdateInsiderInfo } =
        useMatterFieldGates();
    const isUserPrivilegedToUpdateFirmWideAccess = useHasPermissions(['UPDATE.MATTERS.FIRM_WIDE_ACCESS']);
    const isUserPrivilegedToUpdateDmsLink = useHasPermissions(['UPDATE.MATTERS.DMS_LINK']);
    const currentMatterStatus = matter?.matterStateTypeCode;

    const isChangingToOfferWon =
        isOfferFlowEnabled && isStatusChangingToOfferWon(isEditMode, matterStateType?.value, currentMatterStatus);
    // Offer Won stays selectable on a case that already carries it, so switching the flow off cannot make an
    // existing case unsaveable through its own status field.
    const statusOptions = useMemo(() => {
        const options =
            (getValidOptionsFromClassifier(classifiers['MATTER_STATE_TYPE']) as {
                label: string;
                value: string;
            }[]) ?? [];
        if (isOfferFlowEnabled || currentMatterStatus === MatterStateType.OFFER_WON) {
            return options;
        }
        return options.filter((option) => option.value !== MatterStateType.OFFER_WON);
    }, [classifiers, isOfferFlowEnabled, currentMatterStatus]);

    // Case type is a firm-defined taxonomy: a tenant that has not populated the classifier gets no field at all.
    const matterTypeOptions = useMemo(
        () =>
            (getValidOptionsFromClassifier(classifiers['MATTER_TYPE']) as {
                label: string;
                value: string;
            }[]) ?? [],
        [classifiers]
    );
    // A case that already carries a type keeps the field even once the firm retires every value, so the stored
    // value stays visible and correctable instead of disappearing from the form that owns it.
    const showMatterType = matterTypeOptions.length > 0 || initialData?.matterType != null;
    // A firm that has retired every case type can no longer satisfy a mandatory field, so the requirement
    // follows the options rather than the flag alone.
    const isMatterTypeRequired = isMatterTypeMandatory && !isAdministrativeMatter && matterTypeOptions.length > 0;

    // Without the offer flow, only Completed carries a closing date, as it did before Offer Lost gained one.
    const requiresClosedDate = isOfferFlowEnabled
        ? isClosingStatus(matterStateType?.value)
        : matterStateType?.value === MatterStateType.COMPLETED;
    // Update used data after adding new person
    const onAddNewContactPerson = (
        fieldName: 'judgeParties' | 'primaryContactParty' | 'primaryRecipientParty' | 'secondaryRecipientParties',
        newContact: ContactMinDto
    ) => {
        if (fieldName === 'judgeParties') {
            const fieldData = getValues(fieldName);
            fieldData?.push(newContact);
            setValue(fieldName, fieldData);
            return;
        }

        if (fieldName === 'primaryContactParty') {
            setValue(fieldName, { id: newContact.id, name: newContact.name });
            return;
        }

        if (fieldName === 'primaryRecipientParty') {
            setValue(fieldName, {
                id: newContact.id,
                name: newContact.name,
                email: newContact?.mainEmail || '',
            });
            return;
        }

        if (fieldName === 'secondaryRecipientParties') {
            const values = getValues('secondaryRecipientParties');
            values.push({ id: newContact.id, name: newContact.name, email: newContact.mainEmail });
            setValue(fieldName, values);
            return;
        }
    };

    const openAddNewContactModal = (
        fieldName: 'judgeParties' | 'primaryContactParty' | 'primaryRecipientParty' | 'secondaryRecipientParties',
        requireEmail?: boolean
    ): void => {
        const preSelectedEmployer =
            fieldName !== 'judgeParties' && clientParty?.partyType === PartyType.COMPANY
                ? (clientParty as ContactMinDto)
                : undefined;
        openModal(
            <AddContactModal
                onContactAdd={(newContact) => onAddNewContactPerson(fieldName, mapToContactMinDto(newContact))}
                onClose={closeModal}
                contactType={PartyType.PERSON}
                preSelectedEmployer={preSelectedEmployer}
                requireEmail={requireEmail}
            />,
            closeModal,
            FORM_OVERFLOW_LARGE_MODAL_STYLE
        );
    };

    const onAddNewGroupMember = (newContact: ContactMinDto, fieldName: NewContactField) => {
        const fieldData = getValues(fieldName);
        if (Array.isArray(fieldData)) {
            fieldData?.push(newContact);
            setValue(fieldName, fieldData);
        } else {
            setValue(fieldName, newContact);
        }
    };

    const openContactTypePicker = (fieldName: NewContactField): void => {
        openModal(
            <ContactTypePicker
                onCancel={closeModal}
                onSelect={(contactType) => {
                    closeModal();
                    openModal(
                        <AddContactModal
                            onContactAdd={(value) => onAddNewGroupMember(mapToContactMinDto(value), fieldName)}
                            onClose={closeModal}
                            contactType={contactType}
                            preSelectedEmployer={undefined}
                            preSelectedEmployee={undefined}
                        />,
                        closeModal,
                        FORM_OVERFLOW_LARGE_MODAL_STYLE
                    );
                }}
            />,
            closeModal,
            { width: '400px' }
        );
    };

    const openCustomizeInvoiceModal = (): void => {
        const selectedInvoiceTemplate = getValues('invoiceTemplate');
        const selectedInvoiceSpecTemplate = getValues('invoiceSpecTemplate');
        const customization = supportedCustomization(
            invoiceTemplates?.find((t) => t.id === selectedInvoiceTemplate?.id),
            invoiceSpecTemplates?.find((t) => t.id === selectedInvoiceSpecTemplate?.id),
            getValues('invoiceCustomization')
        );

        openModal(
            <CustomizeInvoiceModal
                customization={customization}
                onClose={closeModal}
                customizeMutation={(dto) => {
                    setValue('invoiceCustomization', dto, { shouldDirty: true });
                    return { unwrap: () => Promise.resolve({}) };
                }}
            />,
            undefined,
            { width: 'max-content', maxWidth: 'calc(100vw - 2rem)' }
        );
    };

    // The editor hands back every staged tab and the referenced template; nothing is written until the save.
    const onHourlyRatesChange = (value: StagedPricelists) => {
        setValue('pricelists', value.tabs, { shouldDirty: true });
        setValue('keepsServerCopy', value.keepsServerCopy);
        setValue('referencedPricelist', value.referencedPricelist, { shouldDirty: true });
        clearErrors('pricelists');
        // An opening-date error blamed on a list the editor just re-dated or removed clears at once.
        if (errors.openedDate) {
            void trigger('openedDate');
        }
    };

    const onFeeArrangementChange = useCallback(
        (
            option: {
                label: string;
                value: string;
            } | null
        ) => {
            if (
                option?.value === PricingLimitationType.FIXED_ONETIME ||
                option?.value === PricingLimitationType.FIXED_RECURRING
            ) {
                setValue('isSpecIncludedInInvoice', false);
            } else {
                setValue('isSpecIncludedInInvoice', isCaseMergeInvoiceSpecEnabled);
            }

            if (option?.value === PricingLimitationType.PRO_BONO) {
                setValue(
                    'invoicingIntervalType',
                    invoicingIntervalTypeOptions.find((val) => val.value === 'MANUAL') ?? null
                );
            }
        },
        [invoicingIntervalTypeOptions, isCaseMergeInvoiceSpecEnabled, setValue]
    );

    const storedCurrency = initialData?.pricelistCurrencyType ?? null;
    const { applyCurrencyChange, currencyChangeNotice, endsFollowing } = useCurrencyChangePricing(
        storedCurrency?.value ?? null,
        getValues,
        setValue
    );
    const { confirmCurrencyChange } = useCaseRepricingConfirmation(isEditMode ? matter?.id : undefined);

    const handleOnPricelistCurrencyChange = useCallback(
        (pricelistCurrency: { label: string; value: string } | null) => {
            const apply = () => {
                applyCurrencyChange(pricelistCurrency?.value ?? null);
                const paymentCurrency = getValues('paymentCurrencyType');
                if (pricelistCurrency && pricelistCurrency.value === paymentCurrency?.value) {
                    setValue('paymentCurrencyType', null);
                }
            };
            // Leaving the followed list's currency ends the following and re-prices the uninvoiced work, as a kind
            // switch does, so it asks first; a cancel puts the stored currency back.
            const leavesFollowedList =
                isEditMode &&
                !!getValues('followsPricelist') &&
                !!pricelistCurrency &&
                pricelistCurrency.value !== storedCurrency?.value;
            if (leavesFollowedList) {
                void confirmCurrencyChange(pricelistCurrency.label, apply, () =>
                    setValue('pricelistCurrencyType', storedCurrency)
                );
            } else {
                apply();
            }
        },
        [applyCurrencyChange, confirmCurrencyChange, getValues, isEditMode, setValue, storedCurrency]
    );

    // The staged lists set the earliest date the case may open on; the editor refuses the reverse move too.
    const validateOpenedDate = useCallback(
        (value: unknown) => {
            const openedDate =
                value instanceof Date
                    ? format(value, DEFAULT_DATE_FORMAT_PATTERN)
                    : typeof value === 'string'
                      ? value
                      : null;
            const blocking = datedTabBlockingOpeningDate(getValues('pricelists'), openedDate);
            if (!blocking?.validFrom) {
                return true;
            }
            const formatDate = (date: string) => format(parseISO(date), effectiveFormat);
            return t('matters.opening-date-before-pricelist', {
                date: formatDate(blocking.validFrom),
                name: tabLabel(blocking, openedDate, formatDate, (date) =>
                    t('pricelists.case-editor.from-date', { date })
                ),
            });
        },
        [getValues, effectiveFormat]
    );

    const handleOnPaymentCurrencyCheckboxChange = useCallback(
        (value: boolean) => {
            if (!value) {
                setValue('paymentCurrencyType', null);
            }
        },
        [setValue]
    );

    // Prefill invoiced by office field
    const prefillInvoicedByOffice = (office: NamedEntityMinDto | null) => {
        setValue('invoicedByOffice', office);
    };

    // If description only consists of old case name and current client name then update it with new case name
    // If description only consists of current case name and old client name then update it with new client name
    const prefillDescriptionOnFieldChange = (
        val: string | { id: number; name: string } | undefined | null,
        fieldName: 'caseName' | 'clientName'
    ) => {
        const [caseDescription] = getValues(['description']);

        const newState: { prev: string | null; current: string | null } = {
            prev: null,
            current: null,
        };

        const valToUse = val && typeof val === 'object' ? val.name : typeof val === 'string' ? val?.trim() : null;

        if (fieldName === 'caseName') {
            if (caseNameState.current !== null && caseNameState.current !== valToUse) {
                newState.prev = caseNameState.current;
            }
            newState.current = valToUse || null;
        } else if (fieldName === 'clientName') {
            if (clientNameState.current !== null && clientNameState.current !== valToUse) {
                newState.prev = clientNameState.current;
            }
            newState.current = valToUse || null;
        }

        const oldGeneratedDescription =
            fieldName === 'caseName'
                ? [clientNameState.current, newState.prev].filter(Boolean).join(', ')
                : [newState.prev, caseNameState.current].filter(Boolean).join(', ');

        if (
            !caseDescription ||
            !caseDescription.trim().length ||
            oldGeneratedDescription === caseDescription ||
            newState.prev === caseDescription
        ) {
            const returnString =
                fieldName === 'caseName'
                    ? [clientNameState.current, newState.current].filter(Boolean).join(', ')
                    : [newState.current, caseNameState.current].filter(Boolean).join(', ');
            setValue('description', returnString, { shouldDirty: true });
        }

        if (fieldName === 'caseName') {
            setCaseNameState(newState);
            // Also prefill case name on invoice field
            prefillInvoiceNameOnCaseNameChange(newState);
        } else if (fieldName === 'clientName') {
            setClientNameState(newState);
        }
    };

    const prefillInvoiceNameOnCaseNameChange = (caseNameState: { prev: string | null; current: string | null }) => {
        const [caseNumber, nameOnInvoice] = getValues(['readOnlyCaseNumber', 'invoiceMatterName']);

        const oldGeneratedInvoiceName = [caseNumber, caseNameState.prev].filter(Boolean).join(' ');

        let newInvoiceName = nameOnInvoice || '';
        if (
            !nameOnInvoice ||
            !nameOnInvoice.trim().length ||
            oldGeneratedInvoiceName === nameOnInvoice ||
            caseNameState.prev === nameOnInvoice
        ) {
            newInvoiceName = [caseNumber, caseNameState.current].filter(Boolean).join(' ');
            setValue('invoiceMatterName', newInvoiceName, { shouldDirty: true });
        }
    };

    const resetClientPartyDependentFields = useCallback(() => {
        const [caseNumber, nameOnInvoice, nameOnInvoiceLocalLanguage] = getValues([
            'readOnlyCaseNumber',
            'invoiceMatterName',
            'invoiceMatterNameLocalLanguage',
        ]);

        const removeCaseNumberPrefix = (name: string): string => {
            const trimmedName = name.trim();
            if (caseNumber && trimmedName.startsWith(caseNumber)) {
                return trimmedName
                    .substring(caseNumber.length)
                    .replace(/^[\s\-:]+/, '')
                    .trim();
            }
            return trimmedName;
        };

        if (nameOnInvoice && nameOnInvoice.trim().length) {
            setValue('invoiceMatterName', removeCaseNumberPrefix(nameOnInvoice));
        }

        if (nameOnInvoiceLocalLanguage && nameOnInvoiceLocalLanguage.trim().length) {
            setValue('invoiceMatterNameLocalLanguage', removeCaseNumberPrefix(nameOnInvoiceLocalLanguage));
        }

        setValue('serviceAgreementContract', null);
        setValue('primaryContactParty', null);
        setValue('representedClients', []);
        setValue('primaryRecipientParty', null);
        setValue('primaryRecipientAdditionalEmails', null);
        setValue('secondaryRecipientParties', []);
        setValue('secondaryRecipientAdditionalEmails', null);
        setValue('matterClientReference', null);
        setValue('accountingRevenueType', null);
        setValue('payerParty', null);
        setValue('payers', [emptyPrimaryPayerRow()]);
        setValue('pricingLimitationType', null);
        setValue('invoiceTemplate', null);
        setValue('invoiceSpecTemplate', null);
        setValue('invoiceCustomization', null);
        // Assistants are seeded from the client, so they are client-derived like the fields above:
        // leaving them would both keep the old client's people and block the new client's seed,
        // which only fires while the field is empty. Those the seed added to Handled by leave it with
        // them, while they are still assistants.
        const assistantIds = getValues('administrators').map(({ id }) => id);
        const takenBack = (clientStaffingPrefillRef.current.administrators ?? []).filter((id) =>
            assistantIds.includes(id)
        );
        setValue('administrators', []);
        // The staffing seeded from the old client goes the same way, but only while it is still the
        // seeded person: a supervisor or lawyer the user picked is not client-derived and stays.
        (['supervisedByEmployee', 'responsibleEmployee'] as const).forEach((field) => {
            const seeded = clientStaffingPrefillRef.current[field];
            if (!seeded || getValues(field)?.id !== seeded.id) return;
            setValue(field, null);
            if (seeded.joinedHandledBy) takenBack.push(seeded.id);
        });
        // Someone still Supervised by or Responsible lawyer stays in Handled by, whatever seat the seed gave them.
        const kept = [getValues('supervisedByEmployee')?.id, getValues('responsibleEmployee')?.id];
        if (takenBack.length) {
            setValue(
                'employees',
                getValues('employees').filter(({ id }) => !takenBack.includes(id) || kept.includes(id))
            );
        }
        clientStaffingPrefillRef.current = {};
    }, [getValues, setValue]);

    const handlePracticeAreaChange = useCallback(
        (practiceArea: PracticeArea | null, reason: MultiSelectChangeReason) => {
            const secondLevelPracticeAreas = getValues('secondLevelPracticeAreas');
            const dmsPracticeArea = getValues('dmsPracticeArea');

            if (reason === 'clear') {
                if (secondLevelPracticeAreas.length > 0) {
                    setValue('secondLevelPracticeAreas', [], { shouldDirty: true });
                }
                if (dmsPracticeArea) {
                    setValue('dmsPracticeArea', null, { shouldDirty: true });
                }
                return;
            }

            if (practiceArea === null) {
                return;
            }

            // `copiedPracticeAreas` leaves the same behind on a prefill: the two change together.
            const hasSecondLevelPracticeArea = Boolean(
                secondLevelPracticeAreas.find((pa) => pa.parentPracticeArea?.id === practiceArea.id)
            );

            if (hasSecondLevelPracticeArea) {
                setValue(
                    'secondLevelPracticeAreas',
                    secondLevelPracticeAreas.filter((pa) => pa.parentPracticeArea?.id !== practiceArea.id),
                    { shouldDirty: true }
                );
            }

            if (
                dmsPracticeArea &&
                (practiceArea.id === dmsPracticeArea.id || practiceArea.id === dmsPracticeArea.parentPracticeArea?.id)
            ) {
                setValue('dmsPracticeArea', null, { shouldDirty: true });
            }
        },
        [setValue, getValues]
    );

    // A user's pick of the first area the DMS field offers (an area, or a sub-area with SUB_PRACTICE_AREA) is the
    // area for DMS too when that is empty. A pick that joins areas already there leaves it as it is.
    const prefillDmsPracticeArea = useCallback(
        (picked: PracticeArea, offered: PracticeArea[]) => {
            if (offered.length === 1 && offered[0].id === picked.id && !getValues('dmsPracticeArea')) {
                setValue('dmsPracticeArea', picked, { shouldDirty: true });
            }
        },
        [getValues, setValue]
    );

    const handlePracticeAreaSelection = useCallback(
        (selectedPracticeArea: PracticeArea) => {
            if (!isSubPracticeAreaEnabled) {
                prefillDmsPracticeArea(selectedPracticeArea, getValues('practiceAreas'));
            }
        },
        [isSubPracticeAreaEnabled, prefillDmsPracticeArea, getValues]
    );

    const handleSecondLevelPracticeAreaChange = useCallback(
        (practiceArea: PracticeArea | null, reason: MultiSelectChangeReason) => {
            const dmsPracticeArea = getValues('dmsPracticeArea');
            if (reason === 'clear' && dmsPracticeArea) {
                setValue('dmsPracticeArea', null, { shouldDirty: true });
            }
            if (practiceArea && dmsPracticeArea) {
                if (practiceArea.id === dmsPracticeArea.id) {
                    setValue('dmsPracticeArea', null, { shouldDirty: true });
                }
            }
        },
        [getValues, setValue]
    );

    const handleSecondLevelPracticeAreaSelection = useCallback(
        (selectedPracticeArea: PracticeArea) => {
            const currentPracticeAreas = getValues('practiceAreas') || [];
            const practiceAreasWithParent = practiceAreasWithParentOf(
                selectedPracticeArea,
                currentPracticeAreas,
                isSinglePracticeArea
            );
            if (practiceAreasWithParent === null) {
                // The field has stored the pick by now, so a refused one is taken out again.
                setValue(
                    'secondLevelPracticeAreas',
                    getValues('secondLevelPracticeAreas').filter(({ id }) => id !== selectedPracticeArea.id),
                    { shouldDirty: true }
                );
                return;
            }
            if (practiceAreasWithParent !== currentPracticeAreas) {
                setValue('practiceAreas', practiceAreasWithParent, { shouldDirty: true });
            }
            prefillDmsPracticeArea(selectedPracticeArea, getValues('secondLevelPracticeAreas'));
        },
        [getValues, setValue, isSinglePracticeArea, prefillDmsPracticeArea]
    );

    // The DMS field offers every area it can take while the case has none of them yet, so its pick chooses the case's
    // area as well: the sub-area and the area it sits under with SUB_PRACTICE_AREA, the area without it.
    const handleDmsPracticeAreaSelection = useCallback(
        (selectedPracticeArea: PracticeArea | null) => {
            if (!selectedPracticeArea) {
                return;
            }
            if (!isSubPracticeAreaEnabled) {
                setValue('practiceAreas', [selectedPracticeArea], { shouldDirty: true });
                return;
            }
            const currentPracticeAreas = getValues('practiceAreas') || [];
            const practiceAreasWithParent = practiceAreasWithParentOf(
                selectedPracticeArea,
                currentPracticeAreas,
                isSinglePracticeArea
            );
            if (practiceAreasWithParent === null) {
                setValue('dmsPracticeArea', null, { shouldDirty: true });
                return;
            }
            setValue('secondLevelPracticeAreas', [selectedPracticeArea], { shouldDirty: true });
            if (practiceAreasWithParent !== currentPracticeAreas) {
                setValue('practiceAreas', practiceAreasWithParent, { shouldDirty: true });
            }
        },
        [getValues, setValue, isSubPracticeAreaEnabled, isSinglePracticeArea]
    );

    const groupPrimaryContacts = (contact: ContactMinDtoWithRelationsDto): string | null => {
        if (!clientParty || clientParty.partyType !== PartyType.COMPANY) {
            return null;
        }

        const isEmployee =
            contact.relatesTo?.some(
                (rel) =>
                    rel.partyRelationshipTypeCode === PartyRelationshipTypeCode.EMPLOYEE &&
                    rel.otherParty.id === clientParty.id
            ) ?? false;

        return isEmployee ? t('field.employees') : t('field.other');
    };

    const invoiceRecipientQueryCompanyIds = [
        ...new Set(
            [
                payerParty?.partyType === PartyType.COMPANY ? payerParty.id : undefined,
                clientParty?.partyType === PartyType.COMPANY ? clientParty.id : undefined,
            ].filter(Boolean)
        ),
    ];

    const groupInvoiceRecipients = (option: ContactMinDtoWithRelationsDto): string | null => {
        const isEmployee =
            option.relatesTo?.some(
                (rel) =>
                    rel.partyRelationshipTypeCode === PartyRelationshipTypeCode.EMPLOYEE &&
                    rel.otherParty.id === clientParty?.id
            ) ?? false;

        if (isEmployee && clientParty) {
            return clientParty.name;
        }

        const payer = option.relatesTo?.some(
            (rel) =>
                rel.partyRelationshipTypeCode === PartyRelationshipTypeCode.EMPLOYEE &&
                rel.otherParty.id === payerParty?.id
        );

        if (payer && payerParty) {
            return payerParty.name;
        }

        return t('field.other');
    };

    const formatServiceAgreementLabel = (option: ContractListItemMinDto): string => {
        const createdAt = option.createdAt ? format(new Date(option.createdAt), effectiveFormat) : '';
        const signedAt = option.contractSignedAt ? format(new Date(option.contractSignedAt), effectiveFormat) : '';

        const priceList =
            option.pricelistCurrencyTypeCode && option.minHourlyPrice != null && option.maxHourlyPrice != null
                ? formatRateRange(
                      option.minHourlyPrice,
                      option.maxHourlyPrice,
                      option.avgHourlyPrice,
                      option.pricelistCurrencyTypeCode
                  )
                : '';

        const discountPct = option.discountPct || '';

        const dateSegments = [
            createdAt ? `${t('contracts.created')}: ${createdAt}` : '',
            signedAt ? `${t('contracts.signed')}: ${signedAt}` : '',
        ].filter(Boolean);
        const datePart = dateSegments.length > 0 ? `[${dateSegments.join(', ')}]` : '';

        const parts = [
            option.contractName,
            datePart,
            priceList ? `[${priceList}]` : '',
            discountPct ? `[${discountPct}%]` : '',
        ].filter(Boolean);

        return parts.join(' ');
    };

    const isLoading = isLoadingInvoiceTemplates || isLoadingInvoiceSpecTemplates || isLoadingXRs;

    return {
        allowedRelationshipTypes,
        canManagePayers,
        canReadInsiderInfo,
        canUpdateInsiderInfo,
        changeReasonFields,
        classifiers,
        clientParty,
        clientPartyFilter,
        clientPartyFullData,
        currencyAccessible,
        currencyChangeNotice,
        endsFollowing,
        currentMatterStatus,
        customFields,
        customFieldValuesOf,
        formatServiceAgreementLabel,
        getOptionLabelWithPartyNumberAndEmail,
        groupInvoiceRecipients,
        groupPrimaryContacts,
        handleOnPaymentCurrencyCheckboxChange,
        handleOnPricelistCurrencyChange,
        handleDmsPracticeAreaSelection,
        handlePracticeAreaChange,
        handlePracticeAreaSelection,
        handlePrimaryContactChange,
        handleSecondLevelPracticeAreaChange,
        handleSecondLevelPracticeAreaSelection,
        hasAnyCreateMatterPermission,
        hasConnectedInvoices,
        hasContractSelectionChangedRef,
        hasCreateMatters,
        hasStoredPayerList,
        hideDontNullifyOption,
        invoiceRecipientQueryCompanyIds,
        invoiceSpecTemplates,
        invoiceTemplates,
        invoicingIntervalType,
        invoicingIntervalTypeOptions,
        isAccountingRevenueTypeEnabled,
        isAdministrativeMatter,
        administratorFieldLabel,
        isAdministratorFieldEnabled,
        isAdministratorMandatoryForClients,
        isChangingToOfferWon,
        isConfidential,
        isDMSPracticeAreaMandatory,
        isLinkDraftContractsEnabled,
        isLoading,
        isLoadingNewClientData,
        isMatterTypeRequired,
        isNewPersonaNonGrataClient,
        isNoOpponent,
        isOfferFlowEnabled,
        isOneDriveIntegrationEnabled,
        isOpenedFromOfferWonCase,
        isDraft,
        isPaymentCurrencyDifferent,
        isPracticeAreaMandatoryOnCase,
        isPrimaryContactRequired,
        isRemunerationFieldEditable,
        isResponsibleLawyerNotMandatory,
        isServiceAgreementRequired,
        isSubPracticeAreaEnabled,
        isSharedPaymentEnabled,
        isSpecIncludedInInvoice,
        isTimecardIncrementControlEnabled,
        isTimecardIncrementTouchedRef,
        isUserPrivilegedToUpdateBlockedFields,
        isUserPrivilegedToUpdateDmsLink,
        isUserPrivilegedToUpdateFirmWideAccess,
        isUserPrivilegedToUpdateNonBillableHoursBehavior,
        mapCustomFieldsToDto,
        matter,
        matterStateType,
        matterTypeOptions,
        methods,
        officeInvoiceTemplates,
        officeSpecTemplates,
        onFeeArrangementChange,
        onHourlyRatesChange,
        openAddNewContactModal,
        openContactTypePicker,
        openCustomizeInvoiceModal,
        practiceAreaForDMSOptions,
        prefillDescriptionOnFieldChange,
        prefillHandledBy,
        prefillInvoicedByOffice,
        prefillTemplates,
        pricelistCurrencyType,
        pricingLimitationType,
        pricingLimitationTypeOptions,
        requiresClosedDate,
        resetClientPartyDependentFields,
        secondLevelPracticeAreasFilter,
        secondLevelPracticeAreasNarrowing,
        serviceAgreementContract,
        shouldShowRemunerationField,
        showMatterType,
        showOfferEstimateFields,
        shownPricingKind,
        statusOptions,
        timecardIncrementOptions,
        type,
        validateOpenedDate,
        xrs,
    };
};

export type MatterFormState = ReturnType<typeof useMatterForm>;
